import { AppState, withAuth0, WithAuth0Props } from "@auth0/auth0-react";
import React from "react";
import Navbar from "react-bootstrap/Navbar";
import Nav from "react-bootstrap/Nav";
import { Link } from "react-router-dom";
import NavDropdown from "react-bootstrap/esm/NavDropdown";
import EditProfileModal from "components/modals/profileModal/EditProfileModal";
import ProfileTag from "../header/ProfileTag";
import { Image } from "react-bootstrap";
import Logo from '../../../assets/full_logo.png'
import './Header.css'
import { PlaidLink, PlaidLinkOnExit, PlaidLinkOnSuccess } from "react-plaid-link";
import { setPlaidTokenData, toggleAPIRequestSuccess, toggleLoading, updateReduxCustomerAccountData } from "redux/actions/system/actions";
import { connect, ConnectedProps } from "react-redux";
import CustomerAccount from "models/account/customer/customerAccount";
import { isAfter, addHours } from 'date-fns';
import idVerificationService from "accessors/idVerificationService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const mapState = (state: AppState, route: any) => {
  const { system } = state;
  const { isLoading, apiRequestSuccess, loggedInAccount } = system;
  return {
    isLoading,
    apiRequestSuccess,
    route,
    loggedInAccount
  };
};

const mapDispatch = {
  toggleLoading: (isLoading: boolean) => toggleLoading(isLoading),
  toggleAPIRequestSuccess: (apiRequestSuccess: boolean) => toggleAPIRequestSuccess(apiRequestSuccess),
  setPlaidTokenData: (token: string, tokenExpirationDate: Date) => setPlaidTokenData(token, tokenExpirationDate),
  updateReduxCustomerAccountData: (customerAccount: CustomerAccount) => updateReduxCustomerAccountData(customerAccount),
}

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & WithAuth0Props;

interface HeaderState {
  showEditProfileModal: boolean;
  customerAccount: CustomerAccount;
}
class Header extends React.Component<Props, HeaderState> {
  constructor(props: any) {
    super(props);

    this.state = {
      showEditProfileModal: false,
      customerAccount: this.props.loggedInAccount
    }
  }

  componentDidMount(): void {
    if (!this.state.customerAccount.identityVerificationFailed &&
      (this.state.customerAccount.plaidToken == undefined || isAfter(new Date(), this.state.customerAccount.plaidTokenExpiration ?? new Date()))) {
      this.props.toggleLoading(true)

      idVerificationService.GetPlaidToken()
        .then(response => {
          this.props.setPlaidTokenData(response, addHours(new Date(), 24));
        })
    }
  }

  componentDidUpdate(previousProps: Props) {
    if (previousProps.loggedInAccount !== this.props.loggedInAccount) {
      this.setState({
        customerAccount: this.props.loggedInAccount
      })
    }
  }

  logout = () => {
    this.props.auth0.logout({ returnTo: window.location.origin });
  };

  resetEditProfileModalState(): void {
    // Resetting the modal values and setting the request success value back to false
    this.setState({ showEditProfileModal: false });
  }

  showEditProfileModal(): void {
    this.setState({ showEditProfileModal: true });
  }

  onSuccess: PlaidLinkOnSuccess = (publicToken, metadata) => {
    this.props.toggleLoading(true)

    idVerificationService.GetVerificationDetails(metadata.link_session_id)
      .then((response: boolean | undefined) => {
        this.props.toggleLoading(false);

        if (response) {
          let account: CustomerAccount = { ...this.state.customerAccount };
          account.identityVerificationFailed = false;
          account.plaidToken = undefined;
          account.plaidTokenExpiration = undefined;
          account.identityVerified = true;
          this.setState({ customerAccount: account }, () => this.props.updateReduxCustomerAccountData(this.state.customerAccount));
        } else {
          let account: CustomerAccount = { ...this.state.customerAccount };
          account.identityVerificationFailed = true;
          account.plaidToken = undefined;
          account.plaidTokenExpiration = undefined;
          account.identityVerified = false;
          this.setState({ customerAccount: account }, () => this.props.updateReduxCustomerAccountData(this.state.customerAccount));
        }
      })
  };

  onExit: PlaidLinkOnExit = (error, metadata) => {
    // log onExit callbacks from Link, handle errors
    // https://plaid.com/docs/link/web/#onexit
    //console.log(error, metadata);
  };

  autoOpenPlaid(): void {
    var plaidBtn = document.getElementsByClassName("btnOpenPlaid")[0] as HTMLElement;
    plaidBtn.click();
  }

  render() {
    let { user } = this.props.auth0;

    // User is undefined initially
    if (!user) {
      return null;
    }

    return (
      <>
        <Navbar expand="lg" variant="dark">
          <Navbar.Brand as={Link} to="/active">
            <Image width={150} src={Logo} fluid />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">

              <Nav.Item>
                <Nav.Link as={Link} to="/active">
                  Active Loans
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link as={Link} to="/closed">
                  Past Loans
                </Nav.Link>
              </Nav.Item>

              <Nav.Item>
                <Nav.Link as={Link} to="/loan/create">
                  Submit Loan
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Navbar.Collapse>
          <Navbar.Collapse
            id="basic-navbar-nav"
            className="justify-content-end"
          >
            <Nav.Item>
              <Nav.Link>
                {!this.props.loggedInAccount.identityVerified && !this.props.loggedInAccount.identityVerificationFailed &&
                  <PlaidLink
                    className="nav-link btnOpenPlaid"
                    token={this.state.customerAccount.plaidToken ?? ''}
                    onSuccess={this.onSuccess}
                    onExit={this.onExit}
                    onLoad={this.autoOpenPlaid}
                  >
                    Verify Identity! <span><FontAwesomeIcon icon="id-card" color="red" size="lg" /></span>
                  </PlaidLink>
                }
                {this.props.loggedInAccount.identityVerificationFailed &&
                  <span style={{color: 'red'}}>ID Verification Failed <FontAwesomeIcon icon="times-circle" color="red" size="lg" /></span>
                }
                {this.props.loggedInAccount.identityVerified &&
                  <span style={{color: 'green'}}>Identity Verified <FontAwesomeIcon icon="check" color="green" size="lg" /></span>
                }
              </Nav.Link>
            </Nav.Item>
            <NavDropdown title={<ProfileTag />} id="user-actions-dropdown" alignRight={true}>
              <NavDropdown.Item onClick={this.showEditProfileModal.bind(this)}>Edit Profile</NavDropdown.Item>

              <NavDropdown.Divider />
              <NavDropdown.Item onClick={this.logout}>Logout</NavDropdown.Item>
            </NavDropdown>
          </Navbar.Collapse>
        </Navbar>
        <EditProfileModal
          showModal={this.state.showEditProfileModal}
          sourcePage={'header'}
          action={'edit'}
          onModalCancel={() => this.setState({ showEditProfileModal: false })}
        />
      </>
    );
  }
}

export default withAuth0(connector(Header))
